import { useState, useMemo } from 'react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Paperclip } from 'lucide-react';
import { StatusBadge, Card } from '@components/ui';
import { expenseCategories } from '@mocks/harcamalar';

const ITEMS_PER_PAGE = 10;

export default function HarcamalarTablosu({ expenses }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState('desc');

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
    };

    const getCategoryVariant = (key) => {
        const map = {
            malzeme: 'secondary',
            iscilik: 'info',
            nakliye: 'warning',
            yemek: 'warning',
            fatura: 'tertiary',
            diger: 'neutral'
        };
        return map[key] || 'default';
    };

    const getCategoryLabel = (key) => expenseCategories.find(c => c.key === key)?.label || key;

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };

    const SortIcon = ({ colKey }) => {
        if (sortKey !== colKey) return null;
        return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };

    const sortedExpenses = useMemo(() => {
        const dataToSort = [...expenses];
        if (!sortKey) return dataToSort;

        return dataToSort.sort((a, b) => {
            let aVal = a[sortKey];
            let bVal = b[sortKey];

            if (sortKey === 'category') {
                aVal = getCategoryLabel(aVal);
                bVal = getCategoryLabel(bVal);
            }

            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [expenses, sortKey, sortDirection]);

    const totalPages = Math.ceil(sortedExpenses.length / ITEMS_PER_PAGE);
    const paginatedExpenses = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedExpenses.slice(start, start + ITEMS_PER_PAGE);
    }, [sortedExpenses, currentPage]);

    const thSortClass = "text-left py-3 px-4 text-sm font-semibold text-text-primary cursor-pointer hover:bg-bg-hover relative group";
    const thClass = "text-left py-3 px-4 text-sm font-semibold text-text-primary";

    if (!expenses || expenses.length === 0) {
        return (
            <Card padding="lg" className="text-center">
                <p className="text-text-muted">Bu tarihte harcama bulunmuyor.</p>
            </Card>
        );
    }

    return (
        <Card padding="none" className="overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="bg-bg-tertiary border-b border-border-light">
                            <th onClick={() => handleSort('date')} className={thSortClass}>
                                <div className="flex items-center justify-start gap-1">Tarih <SortIcon colKey="date" /></div>
                            </th>
                            <th onClick={() => handleSort('category')} className={thSortClass}>
                                <div className="flex items-center justify-start gap-1">Kategori <SortIcon colKey="category" /></div>
                            </th>
                            <th className={thClass}>Açıklama</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary hidden md:table-cell">Ödeme</th>
                            <th onClick={() => handleSort('amount')} className={`text-right py-3 px-4 text-sm font-semibold text-text-primary cursor-pointer hover:bg-bg-hover`}>
                                <div className="flex items-center justify-end gap-1">Tutar <SortIcon colKey="amount" /></div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedExpenses.map((expense, idx) => (
                            <tr
                                key={expense.id}
                                className={clsx(
                                    "border-b border-border-light hover:bg-bg-hover transition-colors",
                                    idx % 2 === 0 ? "bg-bg-secondary" : "bg-bg-primary"
                                )}
                            >
                                <td className="py-3 px-4 text-text-secondary text-sm">
                                    {formatDate(expense.date)}
                                </td>
                                <td className="py-3 px-4">
                                    <StatusBadge
                                        status={getCategoryLabel(expense.category)}
                                        variant={getCategoryVariant(expense.category)}
                                    />
                                </td>
                                <td className="py-3 px-4 text-text-primary">
                                    <div className="flex items-center gap-2">
                                        <span>{expense.description}</span>
                                        {expense.receipt_url && (
                                            <Paperclip size={12} className="text-text-muted shrink-0" title="Evrak eklendi" />
                                        )}
                                    </div>
                                </td>
                                <td className="py-3 px-4 text-text-secondary hidden md:table-cell capitalize">
                                    {expense.paymentMethod}
                                </td>
                                <td className="py-3 px-4 text-right font-medium text-error">
                                    {formatCurrency(expense.amount)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border-light bg-bg-tertiary">
                    <p className="text-sm text-text-secondary">
                        {expenses.length} kayıttan {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, expenses.length)} arası gösteriliyor
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className={clsx(
                                "p-2 rounded-lg transition-colors",
                                currentPage === 1
                                    ? "text-text-muted cursor-not-allowed"
                                    : "text-text-secondary hover:bg-bg-hover"
                            )}
                        >
                            <ChevronLeft size={18} />
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={clsx(
                                    "w-8 h-8 rounded-lg text-sm font-medium transition-colors",
                                    currentPage === page
                                        ? "bg-primary text-white"
                                        : "text-text-secondary hover:bg-bg-hover"
                                )}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className={clsx(
                                "p-2 rounded-lg transition-colors",
                                currentPage === totalPages
                                    ? "text-text-muted cursor-not-allowed"
                                    : "text-text-secondary hover:bg-bg-hover"
                            )}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            )}
        </Card>
    );
}
